'use client';

import { useState } from 'react';
import {
  IconCheck,
  IconChevronDown,
  IconCpu,
  IconCurrencyEuro,
  IconWorld,
  IconX,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { formatCurrency, formatHourlyRate } from '@/lib/utils/currency';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  GPU_PRODUCTS,
  REGION_OPTIONS,
  REGION_GROUPS,
  PRICE_RANGE,
  getAvailableGPUs,
} from '@/lib/data/gpu-products';
import type { AirGPURegion } from '@/lib/integrations/airgpu/types';

export interface GPUFilters {
  gpuType: string | null;
  region: AirGPURegion | null;
  priceRange: [number, number];
  showComingSoon: boolean;
}

interface GPUFilterBarProps {
  filters: GPUFilters;
  onFiltersChange: (filters: GPUFilters) => void;
  resultCount: number;
}

/** Trigger styling: neutral when unset, a primary tint when the filter is applied. */
const triggerClass = (active: boolean) =>
  cn(
    'h-10 px-4 rounded-xl transition-all duration-200 hover:scale-100',
    active
      ? 'border-primary/50 bg-primary/10 text-foreground hover:bg-primary/15'
      : 'bg-card text-muted-foreground hover:text-foreground'
  );

export function GPUFilterBar({ filters, onFiltersChange, resultCount }: GPUFilterBarProps) {
  const [priceOpen, setPriceOpen] = useState(false);
  // The range being dragged, until it is committed to the URL; otherwise the
  // slider shows the applied filter. (Replaces a useEffect that copied the
  // prop into state on every change.)
  const [dragRange, setDragRange] = useState<[number, number] | null>(null);
  const localPriceRange = dragRange ?? filters.priceRange;

  const priceActive =
    filters.priceRange[0] !== PRICE_RANGE.min || filters.priceRange[1] !== PRICE_RANGE.max;
  const hasActiveFilters = filters.gpuType !== null || filters.region !== null || priceActive;

  const clearFilters = () => {
    onFiltersChange({
      gpuType: null,
      region: null,
      priceRange: [PRICE_RANGE.min, PRICE_RANGE.max],
      showComingSoon: true,
    });
  };

  // Only rentable GPUs are offered as a filter: a link to a product nobody can
  // rent is a dead end (same rule as GPU_TYPES in lib/catalog/filters.ts).
  const rentable = getAvailableGPUs();
  const selectedGpu = GPU_PRODUCTS.find((g) => g.id === filters.gpuType);
  const selectedRegion = REGION_OPTIONS.find((r) => r.id === filters.region);
  const priceLabel = `${formatCurrency(filters.priceRange[0])} - ${formatCurrency(filters.priceRange[1])}`;

  const activeFilterCount = [filters.gpuType, filters.region, priceActive].filter(Boolean).length;

  const pills = (
    <>
      {filters.gpuType && (
        <FilterPill
          label={selectedGpu?.name || filters.gpuType}
          onRemove={() => onFiltersChange({ ...filters, gpuType: null })}
        />
      )}
      {filters.region && (
        <FilterPill
          label={selectedRegion?.name || filters.region}
          onRemove={() => onFiltersChange({ ...filters, region: null })}
        />
      )}
      {priceActive && (
        <FilterPill
          label={priceLabel}
          onRemove={() =>
            onFiltersChange({ ...filters, priceRange: [PRICE_RANGE.min, PRICE_RANGE.max] })
          }
        />
      )}
    </>
  );

  return (
    <div className="bg-background/90 backdrop-blur border-b border-border sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-4 py-3">
        {/* Row 1: Results count + Clear filters */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-card rounded-lg border border-border">
              <span className="text-2xl font-bold text-foreground tabular-nums">{resultCount}</span>
              <span className="text-sm text-muted-foreground">
                {resultCount === 1 ? 'GPU' : 'GPUs'} available
              </span>
            </div>
            {hasActiveFilters && (
              <span className="text-xs text-muted-foreground">
                {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} applied
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 px-3 text-muted-foreground text-xs"
            >
              <IconX className="size-3.5" />
              Clear all
            </Button>
          )}
        </div>

        {/* Row 2: Filters + Active Pills */}
        <div className="flex flex-wrap items-center gap-2">
          {/* GPU Type Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className={triggerClass(!!filters.gpuType)}>
                <IconCpu className="size-4 opacity-70" />
                {selectedGpu?.name || 'GPU Type'}
                <IconChevronDown className="size-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 rounded-xl p-1">
              <DropdownMenuLabel className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
                Select GPU
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="my-1" />
              <FilterItem
                label="All GPUs"
                selected={!filters.gpuType}
                onSelect={() => onFiltersChange({ ...filters, gpuType: null })}
              />
              {rentable.map((gpu) => (
                <FilterItem
                  key={gpu.id}
                  label={gpu.name}
                  selected={filters.gpuType === gpu.id}
                  onSelect={() => onFiltersChange({ ...filters, gpuType: gpu.id })}
                />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Region Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className={triggerClass(!!filters.region)}>
                <IconWorld className="size-4 opacity-70" />
                {selectedRegion?.name || 'Region'}
                <IconChevronDown className="size-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto rounded-xl p-1">
              <DropdownMenuLabel className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
                Select Region
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="my-1" />
              <FilterItem
                label="All Regions"
                selected={!filters.region}
                onSelect={() => onFiltersChange({ ...filters, region: null })}
              />
              {REGION_GROUPS.map((group) => (
                <div key={group}>
                  <DropdownMenuLabel className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-2 pt-3 pb-1">
                    {group}
                  </DropdownMenuLabel>
                  {REGION_OPTIONS.filter((r) => r.group === group).map((region) => (
                    <FilterItem
                      key={region.id}
                      label={region.name}
                      selected={filters.region === region.id}
                      onSelect={() => onFiltersChange({ ...filters, region: region.id })}
                    />
                  ))}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Price Range Filter */}
          <DropdownMenu open={priceOpen} onOpenChange={setPriceOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className={triggerClass(priceActive)}>
                <IconCurrencyEuro className="size-4 opacity-70" />
                {priceActive ? priceLabel : 'Price'}
                <IconChevronDown className="size-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 p-5 rounded-xl">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                Base rate per hour
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Before the region and upload bandwidth adjustments.
              </p>
              <div className="space-y-5">
                <Slider
                  value={localPriceRange}
                  onValueChange={(value) => setDragRange(value as [number, number])}
                  onValueCommit={(value) => {
                    setDragRange(null);
                    onFiltersChange({ ...filters, priceRange: value as [number, number] });
                  }}
                  min={PRICE_RANGE.min}
                  max={PRICE_RANGE.max}
                  step={PRICE_RANGE.step}
                  className="w-full"
                />
                <div className="flex items-center justify-between">
                  <div className="px-3 py-2 bg-muted rounded-lg">
                    <span className="text-xs text-muted-foreground">Min</span>
                    <p className="text-sm font-semibold text-foreground tabular-nums">
                      {formatHourlyRate(localPriceRange[0])}
                    </p>
                  </div>
                  <div className="w-8 h-px bg-border" />
                  <div className="px-3 py-2 bg-muted rounded-lg">
                    <span className="text-xs text-muted-foreground">Max</span>
                    <p className="text-sm font-semibold text-foreground tabular-nums">
                      {formatHourlyRate(localPriceRange[1])}
                    </p>
                  </div>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex-1" />

          {/* Active Filter Pills - inline on desktop */}
          {hasActiveFilters && <div className="hidden md:flex items-center gap-2">{pills}</div>}
        </div>

        {/* Active Filter Pills - mobile only */}
        {hasActiveFilters && <div className="flex md:hidden flex-wrap gap-2 mt-3">{pills}</div>}
      </div>
    </div>
  );
}

function FilterItem({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      onClick={onSelect}
      className={cn('rounded-lg mx-1 cursor-pointer text-sm', selected && 'bg-primary/10 text-foreground')}
    >
      <div className="flex items-center justify-between w-full">
        <span>{label}</span>
        {selected && <IconCheck className="size-4 text-primary" />}
      </div>
    </DropdownMenuItem>
  );
}

function FilterPill({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-primary/10 border border-primary/30 rounded-full text-xs font-medium text-foreground transition-colors hover:bg-primary/15">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="p-0.5 text-muted-foreground hover:text-foreground hover:bg-primary/20 rounded-full transition-colors"
      >
        <IconX className="size-3" />
      </button>
    </span>
  );
}
